/**
 * Loads the vehicle teams of a set of tasks in one round-trip, for the screens
 * that read a whole board at once — the command dashboard and the dispatch
 * sheet. Reads go through `src/db/assignment-store.ts`; nothing here talks to
 * Dexie directly.
 *
 * It re-reads off the global reactive store, exactly as `useAssignments()` does.
 * That is not a refinement — it is what makes the shared dispatch image correct.
 * A manager assigns a convoy, closes the dialog and taps "שתף לוואטסאפ"
 * in the same breath; without the subscription the board still held the team
 * list it loaded on mount, and the PNG went out reading "סה"כ 0 רכבים" over a
 * mission that had a full convoy on it.
 */
import { useCallback, useEffect, useState } from 'react';
import { getTeamsForTasks } from '../../db/assignment-store';
import { useStoreEvents } from '../../hooks/use-store-events';
import type { TaskTeam } from '../../types/assignment';

export interface TaskTeamsData {
  teams: TaskTeam[];
  loading: boolean;
  reload: () => Promise<void>;
}

export function useTaskTeams(taskIds: readonly string[]): TaskTeamsData {
  // The identity of the array changes on every render; its contents do not.
  const key = [...taskIds].sort().join(',');
  const [teams, setTeams] = useState<TaskTeam[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setTeams(await getTeamsForTasks(key ? key.split(',') : []));
  }, [key]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    void (async () => {
      const rows = await getTeamsForTasks(key ? key.split(',') : []);
      if (!alive) return;
      setTeams(rows);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [key]);

  // A convoy is written by the assignment store and released by the task store —
  // closing a task hands its crew back without a team row being touched — so
  // both domains have to be heard, the same pair `useAssignments()` listens on.
  useStoreEvents(reload, ['assignments', 'tasks']);

  return { teams, loading, reload };
}
